import {Injectable} from '@angular/core';

import {MatSnackBar} from '@angular/material/snack-bar';

import {TranslateService} from '@ngx-translate/core';

import {ignoreElements, Observable, of, startWith, switchMap} from 'rxjs';

import {EntryStore} from '@stores/entry.store';
import {RouteStore} from '@stores/route.store';
import {UiStore} from '@stores/ui.store';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {
  LocalSubsetUtils as LSU,
  LocalSubsetContextType,
} from '@utils/local-subset-utils';

import {UndoCreateSnackBarComponent} from '@components/undo-create-snackbar/undo-create-snackbar.component';
import {undoTime} from '@constants/app.config';

import {Action} from './action';

/** Adds the section to a given lesson instead of the one in the URL, e.g. while building one in the background. */
export interface SectionTarget {
  lessonPK: string;
  /** No undo snackbar and no focus change; nobody is looking at the lesson */
  quiet?: boolean;
}

export interface CreateSectionParams {
  partialEntry: Partial<PL2.AnySectionEntry>;
  entryType: PL2.EntryType;
  index: number;
  userName?: string;
  scope: PL2.SectionScope;
  target?: SectionTarget;
}

@Injectable({providedIn: 'root'})
export class CreateSectionAction implements Action<
  CreateSectionParams,
  PL2.AnySectionEntry
> {
  constructor(
    private entryStore: EntryStore,
    private routeStore: RouteStore,
    private uiStore: UiStore,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
  ) {}

  execute(params: CreateSectionParams): Observable<PL2.AnySectionEntry> {
    const pK = params.target?.lessonPK ?? this.routeStore.state().containerId;
    const pPK = KU.pKFromString(pK);
    const uEPK = params.target ? undefined : this.routeStore.state().uEPK;
    const ssE = LSU.getUserSubsets(pK, this.entryStore.state(), uEPK)[0];
    const sortables = LSU.retrieveSortableItems(
      {
        ssE: ssE,
        tles: LSU.getScopeEntryPKs(pK, this.entryStore.state(), uEPK),
        includeLoose: true,
        contextType: LocalSubsetContextType.Cousins,
        uEPK: uEPK,
      },
      this.entryStore.state(),
    );
    const position = LSU.generatePos(
      sortables,
      this.entryStore.state(),
      params.index,
      ssE,
    );
    const sectionRequest: Partial<APIM.EntryCreateRequest> = {
      mId: pPK.mId,
      pSK: pPK.sK,
      c: [
        {
          e: {
            ...params.partialEntry,
            t: params.entryType,
            uN: params.userName,
            pos: position,
            sS: params.scope,
          },
        },
      ],
    };
    if (params.target?.quiet) {
      return of(
        this.entryStore.create(sectionRequest)[0] as PL2.AnySectionEntry,
      );
    }
    return this._createSection$(sectionRequest).pipe(
      switchMap((section) =>
        this.snackbar
          .openFromComponent(UndoCreateSnackBarComponent, {
            duration: undoTime,
            panelClass: 'wide-snackbar',
            data: {
              entry: section,
              message: this.translateService.instant(
                'create-section.cardAdded',
              ),
            },
          })
          .onAction()
          .pipe(ignoreElements(), startWith(section)),
      ),
    );
  }

  private _createSection$(
    sectionRequest: Partial<APIM.EntryCreateRequest>,
  ): Observable<PL2.AnySectionEntry> {
    const nSection = this.entryStore.create(sectionRequest)[0];
    this.uiStore.setSectionContainerId(KU.stringFromKey(nSection));
    return of(nSection as PL2.AnySectionEntry);
  }
}
